<script setup>
import { ref, computed } from 'vue'
import { useOrders } from '@/composables/useOrders'
import { createToaster } from '@meforma/vue-toaster'

const props = defineProps({
  isOpen: Boolean,
  filterParams: { type: Object, default: () => ({}) },
  selectedIds: { type: Array, default: () => [] }
})

const emit = defineEmits(['close'])

const { fetchOrdersApi, exportOrdersApi } = useOrders()
const toaster = createToaster({ position: 'top-right', duration: 2000 })

const loading = ref(false)
const exportType = ref('')

const hasFilter = computed(() => !!props.filterParams?.search)

const close = () => emit('close')

const doExport = async (ids) => {
  const blob = await exportOrdersApi({ ids })

  if (blob instanceof Blob && blob.type === 'application/json') {
    const text = await blob.text()
    const result = JSON.parse(text)
    toaster.error(result.message || 'Export thất bại')
    return
  }

  if (blob && !blob.error) {
    const url = window.URL.createObjectURL(new Blob([blob]))
    const link = document.createElement('a')
    link.href = url
    link.setAttribute('download', 'export_orders.xlsx')
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    window.URL.revokeObjectURL(url)
    toaster.success('Export thành công!')
    close()
  } else {
    toaster.error(blob?.message || 'Export thất bại')
  }
}

const exportByFilter = async () => {
  loading.value = true
  exportType.value = 'filter'
  try {
    const res = await fetchOrdersApi({ ...props.filterParams, limit: -1, page: 1 })
    if (!res.success || !res.data?.data?.length) {
      toaster.error('Không có dữ liệu để export')
      return
    }
    const ids = res.data.data.map(o => o.id)
    await doExport(ids)
  } catch {
    toaster.error('Có lỗi xảy ra')
  } finally {
    loading.value = false
    exportType.value = ''
  }
}

const exportSelected = async () => {
  loading.value = true
  exportType.value = 'selected'
  try {
    await doExport(props.selectedIds)
  } catch {
    toaster.error('Có lỗi xảy ra')
  } finally {
    loading.value = false
    exportType.value = ''
  }
}
</script>

<template>
  <div v-if="isOpen" class="export-modal-overlay" @click.self="!loading && close()">
    <div class="export-modal">
      <div class="d-flex justify-content-between align-items-center mb-4">
        <h5 class="mb-0">Export Orders</h5>
        <button class="btn-close" :disabled="loading" @click="close"></button>
      </div>

      <div class="d-flex flex-column gap-3">
        <button
          class="export-option-btn"
          :class="{ active: exportType === 'filter', disabled: !hasFilter }"
          :disabled="loading || !hasFilter"
          @click="exportByFilter"
        >
          <div class="d-flex align-items-center gap-3">
            <div class="export-icon" :class="{ muted: !hasFilter }">
              <span v-if="loading && exportType === 'filter'" class="spinner-border spinner-border-sm"></span>
              <i v-else class="fa fa-filter"></i>
            </div>
            <div class="text-start">
              <div class="fw-semibold">Export theo Filter</div>
              <div class="text-muted small mt-1">Xuất tất cả đơn theo điều kiện lọc đang áp dụng (không giới hạn số lượng)</div>
              <div v-if="!hasFilter" class="text-danger small mt-1 fw-semibold">
                <i class="fa fa-exclamation-triangle me-1"></i> Vui lòng áp dụng filter trước khi export
              </div>
            </div>
          </div>
        </button>

        <button
          class="export-option-btn"
          :class="{ active: exportType === 'selected', disabled: selectedIds.length === 0 }"
          :disabled="loading || selectedIds.length === 0"
          @click="exportSelected"
        >
          <div class="d-flex align-items-center gap-3">
            <div class="export-icon success">
              <span v-if="loading && exportType === 'selected'" class="spinner-border spinner-border-sm"></span>
              <i v-else class="fa fa-check-square-o"></i>
            </div>
            <div class="text-start">
              <div class="fw-semibold">
                Export đơn đã tích
                <span v-if="selectedIds.length > 0" class="badge bg-success ms-1">{{ selectedIds.length }}</span>
                <span v-else class="badge bg-secondary ms-1">0</span>
              </div>
              <div class="text-muted small mt-1">
                <span v-if="selectedIds.length > 0">Xuất {{ selectedIds.length }} đơn hàng đã được chọn</span>
                <span v-else>Chưa có đơn nào được tích chọn</span>
              </div>
            </div>
          </div>
        </button>
      </div>

      <div class="mt-4 pt-3 border-top">
        <button class="btn btn-outline-secondary w-100" :disabled="loading" @click="close">Đóng</button>
      </div>
    </div>
  </div>
</template>

<style scoped>
.export-modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.35);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
}

.export-modal {
  background: #fff;
  border-radius: 12px;
  padding: 28px;
  width: 440px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.18);
  animation: fadeIn 0.2s ease-out;
}

.export-option-btn {
  width: 100%;
  background: #f8fafc;
  border: 2px solid #e2e8f0;
  border-radius: 10px;
  padding: 16px 20px;
  cursor: pointer;
  transition: border-color 0.15s, background 0.15s, box-shadow 0.15s;
  text-align: left;
}

.export-option-btn:hover:not(:disabled) {
  border-color: #6366f1;
  background: #f0f4ff;
  box-shadow: 0 2px 8px rgba(99, 102, 241, 0.1);
}

.export-option-btn.active,
.export-option-btn:focus:not(:disabled) {
  border-color: #6366f1;
  background: #f0f4ff;
  outline: none;
}

.export-option-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.export-icon {
  width: 40px;
  height: 40px;
  background: #e0e7ff;
  color: #4338ca;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  flex-shrink: 0;
}

.export-icon.success {
  background: #d1fae5;
  color: #047857;
}

.export-icon.muted {
  background: #f1f5f9;
  color: #94a3b8;
}

@keyframes fadeIn {
  from { opacity: 0; transform: scale(0.96); }
  to { opacity: 1; transform: scale(1); }
}
</style>
